import { DateTime } from "luxon"
import MarketingCampaignFetcher from "packs/models/fetcher/MarketingCampaignFetcher"

export interface MarketingCampaignParams {
    name: string
    description?: string
    service_types?: string[]
    target_urls?: string[]
    allowed_domains?: string[]
    page_scope_overrides?: Record<string, string>
    canonical_url_aliases?: Record<string, string>
    target_keywords?: MarketingTargetKeyword[]
    keyword_opportunity_settings?: MarketingKeywordOpportunitySettings
    final_conversion_event?: MarketingMeasurementEvent
    memo?: string
    status?: string
}

export interface MarketingTargetKeyword {
    keyword: string
    keyword_cluster?: string
    priority: "high" | "medium" | "low"
    search_intent?: "named" | "comparison" | "method" | "problem" | "implementation" | "service" | "informational" | "other"
    purchase_stage?: "awareness" | "consideration" | "comparison" | "decision" | "existing_customer"
    brand_segment?: "brand" | "non_brand"
    anticipated_user?: string
    search_context?: string
    job_to_be_done?: string
    pains?: string[]
    barriers?: string[]
    anxieties?: string[]
    objections?: string[]
    expected_answer?: string
    decision_evidence?: string
    verification_method?: string
    target_page_url?: string
    target_page_scope?: "service_lp" | "blog_article" | "comparison_article" | "corporate" | "other"
    business_relevance: number
    service_fit?: number
    excluded_terms?: string[]
    non_target_intents?: string[]
    final_conversion_event?: MarketingMeasurementEvent
    target_position?: number
    target_ctr?: number
    memo?: string
}

export type MarketingMeasurementEvent =
    | "article_view"
    | "article_cta_impression"
    | "article_cta_click"
    | "service_lp_arrival"
    | "form_view"
    | "form_start"
    | "form_complete"
    | "booking_confirmed"

export interface MarketingKeywordOpportunitySettings {
    fallback_ctr_curve?: Record<string, number>
    minimum_benchmark_impressions?: number
    minimum_benchmark_queries?: number
}

// 画面・レポートが使う計画の実効値。本番同期施策ではローカル設定（local_planning）を優先する。
export interface MarketingCampaignPlanning {
    target_keywords: MarketingTargetKeyword[]
    target_urls: string[]
    final_conversion_event: MarketingMeasurementEvent | "" | null
    page_scope_overrides: Record<string, string>
    keyword_opportunity_settings: MarketingKeywordOpportunitySettings
}

export interface MarketingCampaignPlanningParams {
    target_keywords?: MarketingTargetKeyword[]
    target_urls?: string[]
    final_conversion_event?: MarketingMeasurementEvent | ""
    page_scope_overrides?: Record<string, string>
    keyword_opportunity_settings?: MarketingKeywordOpportunitySettings
}

export interface MarketingTargetKeywordRequirement {
    target_index: number
    keyword: string
    ready: boolean
    missing_fields: string[]
    missing_labels: string[]
}

// 記事の公開前ゲートで止まる原因になる、獲得目標キーワードの未入力の詳細項目（案内表示用）。
export interface MarketingArticleGateReadiness {
    total: number
    blocked_count: number
    keywords: { keyword: string; missing_fields: string[]; missing_labels: string[] }[]
    note: string
    setting_location: string
}

// GET /campaigns/:id/planning。トップの設定チェックリストが1回で使う状態。
export interface MarketingCampaignPlanningStatus {
    campaign: any
    setup_assessment: {
        version: string
        ready_for_article_proposals: boolean
        configured: Record<string, boolean>
        missing_items: { key: string; label: string; setting_location: string }[]
        target_keyword_requirements: MarketingTargetKeywordRequirement[]
    }
    goal: MarketingCampaignGoal
    connections: {
        total: number
        // 保存済み（scope_confirmed_at あり）の数
        confirmed: number
        // 週次レポートと同じscope判定（ConnectionScopeAssessment）で有効な数。旧APIでは未定義
        valid?: number
        overlaps?: { provider: string; connection_ids: string[]; overlapping_ranges: string[] }[]
        assessment_version?: string
        items: {
            id: string
            provider: string
            provider_label: string
            external_resource_id?: string
            external_resource_name?: string
            scope_confirmed: boolean
            scope_valid?: boolean
            missing_fields?: string[]
            article_only_rejected_reason?: string
        }[]
    }
    external: {
        ga4_key_events: { integration_id: string; external_resource_id?: string; status: string }[]
        article_cta: { tag_status: "installed" | "not_installed" | "unknown" | "none"; observed: boolean; lookback_days: number }
    }
    article_gate_readiness?: MarketingArticleGateReadiness
    capabilities?: Record<string, boolean>
}

export class MarketingPlanningSaveError extends Error {
    constructor(public status: number, message: string, public campaign: MarketingCampaign = null) {
        super(message)
    }
}

export interface MarketingCampaignGoal {
    campaign_id: string
    prompt_text: string
    version: number
    content_digest: string
    updated_at?: string | null
    configured: boolean
}

export interface MarketingPublicRoomLink {
    id: string
    marketing_campaign_id: string
    public_id: string
    source_public_room_id?: string
    data_source: "local" | "production"
    title?: string
    page_url?: string
    public_status?: string
    records_count: number
    last_synced_at?: string
    last_successful_synced_at?: string
    last_sync_attempted_at?: string
    cached_through?: string
    sync_range?: { from?: string; to?: string }
    data_fresh_at?: string
    partial?: boolean
    last_sync_error?: string
    error_classification?: string
}

export interface MarketingPublicRoomContact {
    id: string
    link_id: string
    public_id: string
    data_source: "local" | "production"
    conversion_status: "fixed" | "completed" | "in_progress"
    form_completed?: boolean
    proposal_completed?: boolean
    reservation_fixed?: boolean
    answered_status?: string
    appointment_at?: string
    person_name?: string
    company_name?: string
    email?: string
    tel?: string
    answer_results?: Record<string, any>
    tracking_info?: Record<string, any>
    user_agent?: string
    occurred_at?: string
    converted_at?: string
    source_updated_at?: string
    parent_room_public_id?: string
}

export interface MarketingPublicRoomPayload {
    sync_run?: MarketingPublicRoomSyncStatus
    links: MarketingPublicRoomLink[]
    records: MarketingPublicRoomContact[]
    summary: {
        public_room_count: number
        records_count: number
        page_view?: number
        access_user_num?: number
        pc_num?: number
        sp_num?: number
        form_completed_count?: number
        proposal_completed_count?: number
        reservation_fixed_count?: number
        fixed_count: number
        completed_count: number
        unique_visitor_count: number
        last_synced_at?: string
    }
    daily_stats?: Array<{
        id: string
        link_id: string
        public_id: string
        date: string
        page_view: number
        access_user_num: number
        pc_num: number
        sp_num: number
        form_completed_count: number
        proposal_completed_count: number
        reservation_fixed_count: number
    }>
    synchronization?: {
        sync_range?: { from?: string; to?: string }
        data_fresh_at?: string
        partial: boolean
        last_successful_synced_at?: string
        error_classification?: string
        error_classifications?: string[]
    }
    total: number
    page: number
    per: number
    has_next: boolean
    remote_fetch_available: boolean
    remote_configured: boolean
}

export interface MarketingCampaignSummary {
    from?: string
    to?: string
    period?: {
        from?: string
        to?: string
    }
    summary?: any
    totals?: any
    breakdowns?: any
    daily_stats?: any[]
    integrations?: MarketingIntegrationStatus[]
}

export interface MarketingIntegrationBulkSyncResult {
    sync_status: "queued" | "not_queued" | "already_running"
    queued_count: number
    queued_integration_ids: string[]
    skipped_count: number
    public_pages_sync?: MarketingPublicRoomSyncStatus
    enqueue_errors?: Array<{ integration_id: string; provider: string; error_classification: string }>
}

export interface MarketingPublicRoomSyncStatus {
    id?: string
    status?: string
    link_count?: number
    from?: string
    to?: string
    succeeded_count?: number
    failed_count?: number
    errors?: Array<{ link_id?: string; error_classification: string }>
    analytics_queued_count?: number
    analytics_enqueue_errors?: any[]
}

export interface MarketingOauthCredentialMetadata {
    id: string
    provider: string
    service?: "ga4" | "gsc"
    external_account_id?: string
    account_email?: string
    account_name?: string
    status: string
    requires_reauthentication?: boolean
    scopes?: string[]
    expires_at?: string
    last_authenticated_at?: string
}

export interface MarketingServiceCredentialMetadata {
    provider: string
    credential_type: string
    resource_url: string
    account_identifier: string
    status: string
    last_verified_at?: string
}

export interface MarketingWordpressMedia {
    id: string
    user_group_id: string
    user_id: string
    marketing_campaign_id: string
    marketing_integration_id: string
    wordpress_media_id: number | null
    source_url: string
    filename: string
    mime_type: "image/jpeg" | "image/png" | "image/webp" | "image/gif"
    width: number
    height: number
    alt_text: string
    decorative: boolean
    caption: string
    description: string
    source_type: string
    rights_note: string
    status: string
    selected_for_article: boolean
    wordpress_author_id?: number
    uploaded_at?: string
    wordpress_updated_at?: string
    registration_stage?: string
    registration_error_classification?: string
    last_registration_attempt_at?: string
    created_at?: string
    updated_at?: string
}

export interface WordpressMediaUploadConstraints {
    max_file_size_bytes: number
    max_total_pixels: number
    mime_types: string[]
    extensions: string[]
}

export interface WordpressMediaPayload {
    media?: MarketingWordpressMedia
    warnings?: Array<{ code: string; message: string }>
    error?: boolean
    message?: string
    error_classification?: string
}

export interface MarketingArticleSource {
    source_id: string
    source_origin: "user_input" | "ga4" | "gsc" | "internal_measurement" | "wordpress" | "web_search"
    source_type: string
    title: string
    url?: string
    domain?: string
    author_or_organization?: string
    published_at?: string
    accessed_at?: string
    primary_source: boolean
    publishable: boolean
    confidential: boolean
    summary: string
}

export interface MarketingArticlePublicationOutcomes {
    article_id: string
    article_seo_audit_status: "not_started" | "pending" | "verified" | "warning" | "failed" | "manual_required"
    audit_version: string
    snapshot_version: string
    audits: Array<Record<string, any>>
    measurements: Array<Record<string, any>>
    latest_decision?: {
        final_decision: MarketingArticleOutcomeDecision
        decision_rationale: string[]
        decision_policy_version: string
        data_quality: Record<string, any>
        comparison_context: Record<string, any>
    }
    search_observations: Array<Record<string, any>>
    limitations: string[]
}

export interface MarketingArticleSearchObservationInput {
    query: string
    observed_at: string
    engine: "google" | "bing" | "chatgpt" | "gemini" | "perplexity" | "copilot" | "other"
    locale: string
    country: string
    language: string
    device: "desktop" | "mobile" | "tablet"
    search_surface: "ai_overview" | "ai_mode" | "organic_search"
    citation_status: "cited" | "not_cited" | "unknown"
    citation_url?: string
    observation_conditions?: {
        signed_in?: boolean
        personalization?: string
        browser_mode?: string
        notes?: string
    }
    notes?: string
}

export type MarketingArticleOutcomeDecision =
    | "continue"
    | "scale"
    | "iterate_title_intro"
    | "expand_answer"
    | "add_first_party_evidence"
    | "improve_internal_link"
    | "improve_cta"
    | "resolve_technical_issue"
    | "rollback"
    | "retire"
    | "insufficient_data"

export type MarketingArticleAiModelOption =
    | "gpt-4.1-mini"
    | "gpt-6-luna-high"
    | "gpt-6.1-sol-extra-high"
    | "gpt-5.6-luna-high"
    | "gpt-5.6-sol-extra-high"

export type MarketingArticleAiRoutingMode = "cost_optimized" | "manual" | "legacy"

export interface MarketingArticleAdditionalContextFile {
    file_id: string
    original_filename: string
    content_type: string
    extension: string
    extname: string
    file_size: string
    file_size_byte: number
    is_image_file: false
    image_url?: string | null
    did_delete: false
    can_delete: false
    status: string
}

export interface MarketingArticleProposal {
    id: string
    marketing_campaign_id: string
    marketing_integration_id: string
    status:
        | "collecting_evidence"
        | "researching"
        | "analyzing"
        | "proposed"
        | "needs_evidence"
        | "approved"
        | "queued"
        | "generating"
        | "proofreading"
        | "seo_preflight"
        | "posting"
        | "draft_posted"
        | "failed"
        | "review_required"
    current_phase?: 3 | 4 | 5
    direction: string
    target_product?: string
    target_audience?: string
    article_goal?: string
    revision_instruction?: string
    analytics_period: { from: string; to: string }
    research_mode: "none" | "web_search"
    research_status: string
    research_settings: Record<string, any>
    ai_model_option?: MarketingArticleAiModelOption
    ai_routing_mode?: MarketingArticleAiRoutingMode
    ai_routing_label?: string
    ai_routing_snapshot?: Record<string, any>
    ai_routing_reproducibility?: "known" | "unknown"
    ai_model?: string
    ai_reasoning_effort?: string
    ai_model_label?: string
    ai_max_completion_tokens?: number
    proposal_resume_available?: boolean
    proposal_resume_stage?: string
    weekly_report_id?: string
    weekly_report_period?: { from?: string; to?: string }
    additional_context_file_ids?: string[]
    additional_context_files?: MarketingArticleAdditionalContextFile[]
    ai_answer_opportunity_snapshot?: Record<string, any>
    source_duplicate_warnings?: Array<Record<string, any> | string>
    first_party_evidence: Record<string, any>
    strategy: Record<string, any>
    ai_answer_plan?: Record<string, any>
    ai_answer_plan_status?: "ready" | "needs_evidence" | "technical_review"
    outline: Record<string, any>
    seo_plan: Record<string, any>
    claim_ledger: Array<Record<string, any>>
    originality_assessment: Record<string, any>
    originality_questions: string[]
    editorial_quality_plan_version?: string
    editorial_quality_plan?: Record<string, any>
    quality_plan_migration_approved_at?: string
    quality_plan_migration_approved_by?: string
    quality_plan_migration_reason?: string
    editorial_quality_gate?: {
        version: string
        strict: boolean
        ready: boolean
        checks: Array<{
            code: string
            label: string
            status: "passed" | "failed" | "warning"
            blocking: boolean
            message?: string
        }>
    }
    wordpress_content_snapshot: Record<string, any>
    research_snapshot: Record<string, any>
    featured_media_id?: string
    featured_media_url?: string
    error_classification?: string
    safe_error_message?: string
    approved_digest?: string
    approved_by?: string
    approved_at?: string
    approved_snapshot?: Record<string, any>
    generation_prompt_version?: string
    sanitized_content?: Record<string, any>
    content_digest?: string
    seo_title?: string
    meta_description?: string
    canonical_policy?: "self"
    index_policy?: "index_follow" | "noindex_follow"
    schema_plan?: Record<string, any>
    seo_adapter?: string
    seo_metadata_status?: "pending" | "saved" | "manual_required" | "failed"
    structured_data_status?: "pending" | "saved" | "manual_required" | "failed"
    structured_data_payload?: Record<string, any>
    draft_manual_review_items?: string[]
    wordpress_post_id?: number
    wordpress_post_status?: string
    wordpress_slug?: string
    wordpress_author_id?: number
    wordpress_category_ids?: number[]
    wordpress_tag_ids?: number[]
    wordpress_edit_url?: string
    wordpress_link?: string
    wordpress_modified_gmt?: string
    wordpress_payload_digest?: string
    posted_at?: string
    generation_warnings?: string[]
    publication_status?: "draft" | "publishing" | "published" | "publish_failed" | "review_required"
    seo_audit_status?: "not_started" | "pending" | "verified" | "warning" | "failed" | "manual_required"
    publication_preflight_result?: {
        ready?: boolean
        already_published?: boolean
        checks?: Array<{
            code: string
            label: string
            status: "passed" | "warning" | "failed" | "manual_required"
            blocking: boolean
            message?: string
        }>
        warnings?: string[]
        automatic_items?: string[]
        manual_items?: string[]
    }
    publication_preflight_digest?: string
    publication_preflight_at?: string
    wordpress_remote_snapshot?: Record<string, any>
    wordpress_remote_digest?: string
    wordpress_last_synced_at?: string
    publication_conflicts?: Array<{ field: string; message: string }>
    publish_requested_by?: string
    publish_requested_at?: string
    published_at?: string
    wordpress_published_url?: string
    wordpress_published_gmt?: string
    wordpress_published_date?: string
    wordpress_published_modified?: string
    sitemap_url?: string
    sitemap_submission_status?: string
    sitemap_checked_at?: string
    url_inspection_status?: string
    url_inspection_result?: Record<string, any>
    url_inspected_at?: string
    seo_audit_result?: Record<string, any>
    seo_audited_at?: string
    publication_measurement_plan?: Record<string, any>
    measurement_baseline_snapshot_id?: string
    measurement_status?: "not_configured" | "baseline_recorded" | "pending" | "measured" | "partial"
    next_measurement_at?: string
    latest_measurement_at?: string
    ai_usage_summary?: {
        measurement_status: "measured" | "unavailable"
        input_tokens?: number
        cached_input_tokens?: number
        cache_write_tokens?: number
        output_tokens?: number
        reasoning_tokens?: number
        total_tokens?: number
        web_search_calls?: number
        input_item_count?: number
        retry_count?: number
        estimated_cost_usd?: string | null
        estimated_cost_jpy?: string | null
        currency?: "JPY"
        pricing_status?: "configured" | "unconfigured"
        pricing_version?: string | null
        target_budget_usd?: string | null
        budget_exceeded?: boolean
        over_budget_phases?: string[]
        phases?: Array<{
            phase: string
            model: string
            reasoning_effort?: string | null
            max_output_tokens: number
            input_tokens: number
            output_tokens: number
            reasoning_tokens: number
            input_item_count: number
            web_search_calls: number
            retry_count: number
            request_count: number
            budget_adjusted: boolean
            estimated_cost_usd?: string | null
            estimated_cost_jpy?: string | null
            phase_budget_usd?: string | null
            budget_exceeded: boolean
        }>
    }
    sources?: MarketingArticleSource[]
    created_at?: string
    updated_at?: string
}

export interface MarketingArticleProposalPage {
    articles: MarketingArticleProposal[]
    hasMore: boolean
    nextCursor: string | null
}

export interface MarketingArticleInput {
    marketing_integration_id: string
    weekly_report_id?: string
    additional_context_file_ids?: string[]
    direction: string
    target_product: string
    target_audience: string
    article_goal: string
    revision_instruction?: string
    analytics_period: { from: string; to: string }
    research_mode: "none" | "web_search"
    ai_model_option: MarketingArticleAiModelOption
    ai_routing_mode: Exclude<MarketingArticleAiRoutingMode, "legacy">
    research_settings: {
        primary_source_urls: string[]
        excluded_domains: string[]
        external_only_acknowledged: boolean
    }
    first_party_evidence: Record<string, any>
    featured_media_id?: string
}

export interface MarketingIntegrationStatus {
    id?: string
    user_group_id?: string
    marketing_campaign_id?: string
    data_source?: "local_overlay"
    provider: string
    provider_label: string
    status: string
    external_resource_id?: string
    name?: string
    external_resource_name?: string
    property_id?: string
    property_name?: string
    site_url?: string
    site_type?: "domain" | "url_prefix"
    permission_level?: string
    time_zone?: string
    currency_code?: string
    hostnames?: string[]
    target_urls?: string[]
    measurement_scope?: MarketingMeasurementScope
    scope_confirmed_at?: string
    suggested_measurement_scope?: MarketingMeasurementScopeSuggestion | null
    generative_ai_performance_capability?: {
        api_available: boolean
        official_export_import_available: boolean
        report_access?: string
        limitations?: string[]
    }
    generative_ai_last_imported_at?: string
    oauth_credential?: MarketingOauthCredentialMetadata
    service_credential?: MarketingServiceCredentialMetadata
    last_verified_at?: string
    last_synced_at?: string
    last_sync_attempted_at?: string
    last_sync_period_from?: string
    last_sync_period_to?: string
    last_sync_row_count?: number
    last_sync_error_classification?: string
    last_sync_quota_snapshot?: Record<string, any>
    last_sync_truncated_reports?: string[]
    has_sync_error?: boolean
    error_message?: string
    ai_search_readiness?: AiSearchReadinessAudit | null
    created_at?: string
    updated_at?: string
}

export interface MarketingMeasurementScope {
    data_stream_id?: string
    hostnames?: string[]
    paths?: string[]
    canonical_host?: string
    url_prefixes?: string[]
    article_url_prefixes?: string[]
    public_page_urls?: string[]
    service_lp_urls?: string[]
    time_zone?: string
    key_event_names?: string[]
    key_event_status?: "confirmed" | "unknown" | "not_configured"
    generative_ai_report_access?: "available" | "unavailable" | "unknown"
    // 公開ページURL・サービスLP URLを明示的に空とした記事専用GA4 property
    article_only?: boolean
    // WordPress（記事サイト）の公開HTMLで確認したwaaq.auto.jsタグの設置状況
    article_tag_status?: "installed" | "not_installed" | "unknown"
}

export interface MarketingMeasurementScopeSuggestion {
    version: string
    scope: MarketingMeasurementScope
    sources: Partial<Record<keyof MarketingMeasurementScope, string>>
    manual_fields: Partial<Record<keyof MarketingMeasurementScope, string>>
    note: string
}

export interface AiSearchReadinessCheck {
    code: string
    category: "blocker" | "improvement" | "audit"
    status: "passed" | "warning" | "blocked" | "failed"
    weight: number
    message?: string
    detail?: any
}

export interface AiSearchReadinessAudit {
    id: string
    audit_version: string
    status: "eligible" | "warning" | "blocked" | "failed"
    score: number
    checks: AiSearchReadinessCheck[]
    blocking_codes: string[]
    warning_codes: string[]
    audited_urls: string[]
    max_snippet?: number | null
    capability_snapshot: Record<string, any>
    manual_actions: string[]
    error_classification?: string
    audited_at: string
    expires_at: string
    expired: boolean
}

export interface Ga4PropertyOption {
    property_id: string
    property_name: string
    property_type?: string
    account_id?: string
    account_name?: string
}

export interface GscSiteOption {
    site_url: string
    site_type: "domain" | "url_prefix"
    permission_level: string
    connected: boolean
}

export interface GscDashboardSite {
    connection_id: string
    site_url: string
    site_type?: "domain" | "url_prefix"
    permission_level?: string
    status: string
    sync_state: string
    has_stored_data: boolean
    has_current_data: boolean
    latest_data_date?: string
    last_synced_at?: string
    incomplete_period?: { from?: string; to?: string }
    row_limit_reached?: boolean
    truncated_reports?: string[]
    requires_reauthentication?: boolean
    permission_denied?: boolean
    error_classification?: string
}

export interface GscDashboardPayload {
    period: {
        current: { from: string; to: string }
        previous: { from: string; to: string }
        time_zone: string
    }
    search_type: string
    selection: {
        mode: "all" | "single" | "multiple"
        selected_connection_ids: string[]
        selected_connection_count: number
    }
    data_state: "empty" | "complete" | "partial" | "syncing" | "reauthentication_required"
    partial: boolean
    warnings: any[]
    data_limitations: string[]
    kpis: Record<string, any>
    daily_trend: any[]
    top_clicked_keywords: any[]
    top_impression_keywords: any[]
    ctr_improvement_candidates: any[]
    ranking_changes: { rising: any[]; declining: any[] }
    top_pages: any[]
    query_pages: any[]
    site_breakdown: any[]
    device_breakdown: any[]
    keyword_opportunities: GscKeywordOpportunity[]
    opportunity_notice?: string
    sites: GscDashboardSite[]
}

export interface GscOpportunityMetrics {
    clicks: number
    impressions: number
    ctr: number
    position: number
}

export interface GscKeywordOpportunity {
    query: string
    connection_id: string
    site_url?: string
    site_type?: "domain" | "url_prefix"
    score: number
    score_version: string
    reason_codes: string[]
    estimated_additional_clicks: number | null
    related_pages: Array<{
        page: string
        clicks: number
        impressions: number
    }>
    current: GscOpportunityMetrics
    previous: GscOpportunityMetrics
    difference: GscOpportunityMetrics
    change_rate?: Partial<Record<keyof GscOpportunityMetrics, number | null>>
}

export interface Ga4DashboardMetric {
    value: number
    previous_value: number
    difference: number
    change_rate?: number
    numerator?: number
    denominator?: number
}

export interface Ga4DashboardProperty {
    connection_id: string
    property_id: string
    property_name?: string
    status: string
    sync_state: "ready" | "empty" | "partial" | "error" | "permission_denied" | "paused" | "syncing" | "reauthentication_required"
    has_current_data: boolean
    has_previous_data: boolean
    last_synced_at?: string
    last_sync_attempted_at?: string
    latest_data_date?: string
    hostnames?: string[]
    error_classification?: string
    truncated_reports?: string[]
}

export interface Ga4DashboardPayload {
    period: {
        current: { from: string; to: string }
        previous: { from: string; to: string }
    }
    selection: {
        mode: "single" | "combined"
        selected_connection_ids: string[]
        selected_connection_count: number
    }
    properties: Ga4DashboardProperty[]
    data_state: "empty" | "complete" | "partial" | "syncing" | "reauthentication_required"
    partial: boolean
    warnings: string[]
    user_deduplication_notice?: string
    freshness: {
        latest_data_date?: string
        data_updated_at?: string
        last_synced_at?: string
        oldest_property_sync_at?: string
    }
    hostnames: string[]
    kpis: Record<string, Ga4DashboardMetric>
    rankings: Record<string, any[]>
    waaq_auto: any
    funnel: any[]
}

export interface GscGa4InsightCoverage {
    connection_id: string
    resource_id: string
    resource_name?: string
    status: string
    has_data: boolean
    latest_data_date?: string
    last_synced_at?: string
    missing_reasons: string[]
}

export interface GscGa4InsightPayload {
    period: {
        current: { from: string; to: string }
        previous: { from: string; to: string }
        gsc_time_zone: string
    }
    search_type: string
    selection: {
        gsc_connection_ids: string[]
        ga4_connection_ids: string[]
    }
    data_state: "empty" | "complete" | "partial"
    partial: boolean
    warnings: Array<{
        code: string
        provider: "gsc" | "ga4"
        connection_id?: string
        connection_ids?: string[]
        resource_id?: string
        resource_ids?: string[]
        missing_reasons?: string[]
        message?: string
    }>
    attribution_notice: string
    matching: {
        notice: string
        rules: Record<string, string | boolean>
        evaluated_gsc_query_page_candidate_count: number
        gsc_query_page_candidate_limit: number
        ga4_landing_page_candidate_limit: number
        insight_limit_per_category: number
        count_scope: "evaluated_candidates_only"
        matched_page_count: number
        unmatched_gsc_page_count: number
        unmatched_gsc_pages: any[]
    }
    coverage: {
        gsc: GscGa4InsightCoverage[]
        ga4: GscGa4InsightCoverage[]
    }
    thresholds: Record<string, any>
    insights: {
        high_impressions_low_ctr_high_quality: any[]
        high_clicks_weak_ga4_quality: any[]
        ga4_success_striking_distance: any[]
        rising_queries: any[]
    }
}

export interface MarketingIntegrationParams {
    provider: string
    external_resource_id: string
    name?: string
    external_resource_name?: string
    time_zone?: string
    currency_code?: string
    hostnames?: string[]
    target_urls?: string[]
    marketing_oauth_credential_id?: string
    status?: string
}

export interface WordpressConnectionParams {
    site_url: string
    connection_name: string
    username: string
    application_password: string
}

export interface WordpressMediaUploadParams {
    file: File
    alt_text: string
    decorative: boolean
    caption: string
    description: string
    source_type: string
    rights_note: string
    selected_for_article: boolean
}

export default class MarketingCampaign {
    static readonly STATUS_ACTIVE = "active"
    static readonly STATUS_PAUSED = "paused"
    static readonly STATUS_ARCHIVED = "archived"
    static readonly SERVICE_TYPE_WAAQ_AUTO = "waaq_auto"
    static readonly LEGACY_SERVICE_NAMES: Record<string, string> = {
        waaq_blog: "waaq.blog",
        lp: "LPページ",
    }

    static readonly SERVICE_OPTIONS = [
        { key: "waaq_auto", name: "waaq.auto.js計測" },
        { key: "gsc", name: "Google Search Console" },
        { key: "ga4", name: "GA4" },
        { key: "google_ads", name: "Google広告（将来連携）" },
        { key: "wordpress", name: "WordPress" },
        { key: "other", name: "その他" },
    ]

    constructor(
        public id: string,
        public user_group_id: string,
        public name: string,
        public description: string,
        public marketing_key: string,
        public status: string,
        public service_types: string[],
        public target_urls: string[],
        public allowed_domains: string[],
        public memo: string,
        public last_event_at: string,
        public events_count: number,
        public created_at: string,
        public updated_at: string,
        public summary: MarketingCampaignSummary = null,
        public page_scope_overrides: Record<string, string> = {},
        public target_keywords: MarketingTargetKeyword[] = [],
        public keyword_opportunity_settings: MarketingKeywordOpportunitySettings = {},
        public final_conversion_event: MarketingMeasurementEvent | "" = "",
        public canonical_url_aliases: Record<string, string> = {},
        public planning: MarketingCampaignPlanning = null,
        public local_planning_version: number = 0,
        public planning_source: string = ""
    ) {}

    // 旧APIやテストで planning がない場合は従来fieldを実効値として扱う。
    static planningOf(campaign: MarketingCampaign): MarketingCampaignPlanning {
        const planning = campaign?.planning
        return {
            target_keywords: planning?.target_keywords ?? campaign?.target_keywords ?? [],
            target_urls: planning?.target_urls ?? campaign?.target_urls ?? [],
            final_conversion_event: planning ? planning.final_conversion_event || "" : campaign?.final_conversion_event || "",
            page_scope_overrides: planning?.page_scope_overrides ?? campaign?.page_scope_overrides ?? {},
            keyword_opportunity_settings:
                planning?.keyword_opportunity_settings ?? campaign?.keyword_opportunity_settings ?? {},
        }
    }

    static fetchFromJson(campaignsJson: any[] = []): MarketingCampaign[] {
        return (campaignsJson || []).map(dic => {
            return new MarketingCampaign(
                dic.id,
                dic.user_group_id,
                dic.name,
                dic.description,
                dic.marketing_key,
                dic.status,
                dic.service_types || [],
                dic.target_urls || [],
                dic.allowed_domains || [],
                dic.memo,
                dic.last_event_at,
                dic.events_count || 0,
                dic.created_at,
                dic.updated_at,
                dic.summary || null,
                dic.page_scope_overrides || {},
                dic.target_keywords || [],
                dic.keyword_opportunity_settings || {},
                dic.final_conversion_event || "",
                dic.canonical_url_aliases || {},
                dic.planning || null,
                Number(dic.local_planning_version || 0),
                dic.planning_source || ""
            )
        })
    }

    static fetchCampaigns(params: any = {}): Promise<any> {
        return MarketingCampaignFetcher.fetchCampaigns(params).then(res => {
            if (!res) return { campaigns: [], total: 0, page: 1, per: 30, has_next: false }

            return {
                campaigns: MarketingCampaign.fetchFromJson(res.campaigns || []),
                total: res.total || 0,
                page: res.page || 1,
                per: res.per || 30,
                has_next: !!res.has_next,
                data_source: res.data_source || "local",
                read_only: !!res.read_only,
                campaign_writable: res.campaign_writable ?? res.capabilities?.campaign_writable ?? !res.read_only,
                public_room_links_writable:
                    res.public_room_links_writable ?? res.capabilities?.public_room_links_writable ?? true,
                remote_public_room_fetch_available:
                    res.remote_public_room_fetch_available ?? res.capabilities?.remote_public_room_fetch_available ?? false,
                local_integrations_writable:
                    res.local_integrations_writable ?? res.capabilities?.local_integrations_writable ?? !res.read_only,
                service_integrations_writable:
                    res.service_integrations_writable ?? res.capabilities?.service_integrations_writable ?? !res.read_only,
                ga4_connections_enabled: res.ga4_connections_enabled ?? res.capabilities?.ga4_connections_enabled ?? true,
                gsc_connections_enabled: res.gsc_connections_enabled ?? res.capabilities?.gsc_connections_enabled ?? true,
                campaign_goals_writable:
                    res.campaign_goals_writable ?? res.capabilities?.campaign_goals_writable ?? !res.read_only,
                planning_writable:
                    res.planning_writable ??
                    res.capabilities?.planning_writable ??
                    res.campaign_goals_writable ??
                    res.capabilities?.campaign_goals_writable ??
                    !res.read_only,
                remote_sync_available: !!res.remote_sync_available,
                remote_sync_enabled: !!res.remote_sync_enabled,
                remote_target_user_group_id: res.remote_target_user_group_id || null,
                fetched_at: res.fetched_at || null,
                conversion_confirmation: res.conversion_confirmation || "browser_event_only",
            }
        })
    }

    static syncRemote(params: any = {}): Promise<any> {
        return MarketingCampaignFetcher.syncRemote(params)
    }

    static fetchPublicRooms(id: string, params: any = {}): Promise<MarketingPublicRoomPayload> {
        return MarketingCampaignFetcher.fetchPublicRooms(id, params)
    }

    static fetchGoal(id: string): Promise<MarketingCampaignGoal | null> {
        return MarketingCampaignFetcher.fetchGoal(id).then(res => res?.goal || null)
    }

    static saveGoal(id: string, promptText: string, expectedVersion: number): Promise<MarketingCampaignGoal | null> {
        return MarketingCampaignFetcher.saveGoal(id, promptText, expectedVersion).then(res => res?.goal || null)
    }

    static clearGoal(id: string, expectedVersion: number): Promise<MarketingCampaignGoal | null> {
        return MarketingCampaignFetcher.clearGoal(id, expectedVersion).then(res => res?.goal || null)
    }

    static attachPublicRooms(id: string, params: any): Promise<any> {
        return MarketingCampaignFetcher.attachPublicRooms(id, params)
    }

    static syncPublicRooms(id: string, linkIds: string[] = [], params: any = {}): Promise<any> {
        return MarketingCampaignFetcher.syncPublicRooms(id, linkIds, params)
    }

    static removePublicRoom(id: string, linkId: string): Promise<any> {
        return MarketingCampaignFetcher.removePublicRoom(id, linkId)
    }

    static createCampaign(params: MarketingCampaignParams): Promise<MarketingCampaign> {
        return MarketingCampaignFetcher.createCampaign(params).then(res => {
            if (!res || !res.campaign) return null
            return MarketingCampaign.fetchFromJson([res.campaign])[0]
        })
    }

    static updateCampaign(id: string, params: Partial<MarketingCampaignParams>): Promise<MarketingCampaign> {
        return MarketingCampaignFetcher.updateCampaign(id, params).then(res => {
            if (!res || !res.campaign) return null
            return MarketingCampaign.fetchFromJson([res.campaign])[0]
        })
    }

    // 本番同期モードでもローカル設定として保存する。409（競合）・422（不正値）はMarketingPlanningSaveErrorで返す。
    static updatePlanning(
        id: string,
        params: MarketingCampaignPlanningParams,
        expectedVersion: number
    ): Promise<MarketingCampaign> {
        return MarketingCampaignFetcher.updatePlanning(id, params, expectedVersion)
            .then(res => {
                if (!res || !res.campaign) throw new MarketingPlanningSaveError(0, "保存結果を取得できませんでした。")
                return MarketingCampaign.fetchFromJson([res.campaign])[0]
            })
            .catch(err => {
                if (err instanceof MarketingPlanningSaveError) throw err
                const data = err?.response?.data || {}
                const current = data.campaign ? MarketingCampaign.fetchFromJson([data.campaign])[0] : null
                throw new MarketingPlanningSaveError(
                    err?.response?.status || 0,
                    data.message || "保存に失敗しました。入力内容を確認してください。",
                    current
                )
            })
    }

    static fetchPlanningStatus(id: string): Promise<MarketingCampaignPlanningStatus> {
        return MarketingCampaignFetcher.fetchPlanningStatus(id).then(res => (res as MarketingCampaignPlanningStatus) || null)
    }

    static fetchSummary(id: string, params: any = 28): Promise<MarketingCampaignSummary> {
        let requestParams = params
        if (typeof params == "number") {
            const to = DateTime.local()
            const from = to.minus({ days: Math.max(params - 1, 0) })
            requestParams = {
                from: from.toISODate(),
                to: to.toISODate(),
            }
        }

        return MarketingCampaignFetcher.fetchSummary(id, requestParams).then(res => {
            if (!res) return null
            return res as MarketingCampaignSummary
        })
    }

    static fetchEvents(id: string, params: any = {}): Promise<any> {
        return MarketingCampaignFetcher.fetchEvents(id, params).then(res => {
            if (!res) return { events: [], total: 0, page: 1, per: 10, has_next: false }
            return {
                events: res.events || [],
                total: res.total || 0,
                page: res.page || 1,
                per: res.per || 10,
                has_next: !!res.has_next,
            }
        })
    }

    static fetchGa4Dashboard(id: string, params: any = {}): Promise<Ga4DashboardPayload> {
        return MarketingCampaignFetcher.fetchGa4Dashboard(id, params).then(res => {
            return (res as Ga4DashboardPayload) || null
        })
    }

    static fetchGscDashboard(id: string, params: any = {}): Promise<GscDashboardPayload> {
        return MarketingCampaignFetcher.fetchGscDashboard(id, params).then(res => {
            return (res as GscDashboardPayload) || null
        })
    }

    static fetchGscGa4Insights(id: string, params: any = {}): Promise<GscGa4InsightPayload> {
        return MarketingCampaignFetcher.fetchGscGa4Insights(id, params).then(res => {
            return (res as GscGa4InsightPayload) || null
        })
    }

    static fetchIntegrations(id: string): Promise<MarketingIntegrationStatus[]> {
        return MarketingCampaignFetcher.fetchIntegrations(id).then(res => {
            return (res && res.integrations) || []
        })
    }

    static fetchIntegrationScopeSuggestions(
        id: string
    ): Promise<Record<string, MarketingMeasurementScopeSuggestion>> {
        return MarketingCampaignFetcher.fetchIntegrationScopeSuggestions(id).then(res => {
            const suggestions: Record<string, MarketingMeasurementScopeSuggestion> = {}
            ;((res && res.integrations) || []).forEach((integration: MarketingIntegrationStatus) => {
                if (integration.id && integration.suggested_measurement_scope) {
                    suggestions[integration.id] = integration.suggested_measurement_scope
                }
            })
            return suggestions
        })
    }

    static createIntegration(id: string, params: MarketingIntegrationParams): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.createIntegration(id, params).then(res => {
            return (res && res.integration) || null
        })
    }

    static updateIntegration(
        campaignId: string,
        integrationId: string,
        params: Partial<MarketingIntegrationParams>
    ): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.updateIntegration(campaignId, integrationId, params).then(res => {
            return (res && res.integration) || null
        })
    }

    static updateIntegrationScope(
        campaignId: string,
        integrationId: string,
        scope: MarketingMeasurementScope
    ): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.updateIntegrationScope(campaignId, integrationId, scope).then(res => {
            return (res && res.integration) || null
        })
    }

    static importGenerativeAiPerformance(
        campaignId: string,
        integrationId: string,
        file: File,
        periodStart?: string,
        periodEnd?: string
    ): Promise<any> {
        return MarketingCampaignFetcher.importGenerativeAiPerformance(campaignId, integrationId, file, periodStart, periodEnd)
    }

    static pauseIntegration(campaignId: string, integrationId: string): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.pauseIntegration(campaignId, integrationId).then(res => {
            return (res && res.integration) || null
        })
    }

    static disconnectIntegration(campaignId: string, integrationId: string): Promise<boolean> {
        return MarketingCampaignFetcher.disconnectIntegration(campaignId, integrationId).then(res => {
            return !!(res && res.disconnected_integration_id)
        })
    }

    static connectWordpress(campaignId: string, params: WordpressConnectionParams): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.connectWordpress(campaignId, params).then(res => {
            return (res && res.integration) || null
        })
    }

    static reauthenticateWordpress(
        campaignId: string,
        integrationId: string,
        params: WordpressConnectionParams
    ): Promise<MarketingIntegrationStatus> {
        return MarketingCampaignFetcher.reauthenticateWordpress(campaignId, integrationId, params).then(
            res => (res && res.integration) || null
        )
    }

    static disconnectWordpress(campaignId: string, integrationId: string): Promise<boolean> {
        return MarketingCampaignFetcher.disconnectWordpress(campaignId, integrationId).then(res => {
            return !!(res && res.disconnected_integration_id)
        })
    }

    static auditWordpressAiSearchReadiness(
        campaignId: string,
        integrationId: string,
        url?: string
    ): Promise<AiSearchReadinessAudit | null> {
        return MarketingCampaignFetcher.auditWordpressAiSearchReadiness(campaignId, integrationId, url).then(
            res => (res && res.ai_search_readiness) || null
        )
    }

    static fetchWordpressMedia(campaignId: string, integrationId: string): Promise<any> {
        return MarketingCampaignFetcher.fetchWordpressMedia(campaignId, integrationId)
    }

    static uploadWordpressMedia(
        campaignId: string,
        integrationId: string,
        params: WordpressMediaUploadParams
    ): Promise<WordpressMediaPayload> {
        return MarketingCampaignFetcher.uploadWordpressMedia(campaignId, integrationId, params)
    }

    static updateWordpressMedia(
        campaignId: string,
        mediaId: string,
        params: Partial<WordpressMediaUploadParams>
    ): Promise<WordpressMediaPayload> {
        return MarketingCampaignFetcher.updateWordpressMedia(campaignId, mediaId, params)
    }

    static createArticleProposal(campaignId: string, params: MarketingArticleInput): Promise<MarketingArticleProposal> {
        return MarketingCampaignFetcher.createArticleProposal(campaignId, params).then(res => {
            return (res && res.article) || null
        })
    }

    static updateArticleProposal(
        campaignId: string,
        articleId: string,
        params: Partial<MarketingArticleInput>
    ): Promise<MarketingArticleProposal> {
        return MarketingCampaignFetcher.updateArticleProposal(campaignId, articleId, params).then(res => {
            return (res && res.article) || null
        })
    }

    static fetchArticleProposal(campaignId: string, articleId: string): Promise<MarketingArticleProposal> {
        return MarketingCampaignFetcher.fetchArticleProposal(campaignId, articleId).then(res => {
            return (res && res.article) || null
        })
    }

    static fetchArticleProposals(campaignId: string, cursor?: string): Promise<MarketingArticleProposalPage | null> {
        return MarketingCampaignFetcher.fetchArticleProposals(campaignId, cursor).then(res => {
            if (!res) return null
            return {
                articles: res.articles || [],
                hasMore: !!res.has_more,
                nextCursor: res.next_cursor || null,
            }
        })
    }

    static archiveArticleProposal(campaignId: string, articleId: string): Promise<boolean> {
        return MarketingCampaignFetcher.archiveArticleProposal(campaignId, articleId).then(res => {
            return res?.archived === true
        })
    }

    static approveArticleProposal(
        campaignId: string,
        articleId: string,
        revisionInstruction?: string
    ): Promise<MarketingArticleProposal> {
        return MarketingCampaignFetcher.approveArticleProposal(campaignId, articleId, revisionInstruction).then(res => {
            return (res && res.article) || null
        })
    }

    static generateArticleDraft(campaignId: string, articleId: string): Promise<MarketingArticleProposal> {
        return MarketingCampaignFetcher.generateArticleDraft(campaignId, articleId).then(res => {
            return (res && res.article) || null
        })
    }

    static reviewArticlePublication(
        campaignId: string,
        articleId: string,
        migration?: { confirmation: boolean; reason: string }
    ): Promise<any> {
        return MarketingCampaignFetcher.reviewArticlePublication(campaignId, articleId, migration)
    }

    static adoptWordpressArticle(campaignId: string, articleId: string): Promise<any> {
        return MarketingCampaignFetcher.adoptWordpressArticle(campaignId, articleId)
    }

    static publishArticle(campaignId: string, articleId: string, publicationPreflightDigest: string): Promise<any> {
        return MarketingCampaignFetcher.publishArticle(campaignId, articleId, publicationPreflightDigest)
    }

    static recheckArticleSeo(campaignId: string, articleId: string): Promise<any> {
        return MarketingCampaignFetcher.recheckArticleSeo(campaignId, articleId)
    }

    static fetchArticlePublicationOutcomes(
        campaignId: string,
        articleId: string
    ): Promise<MarketingArticlePublicationOutcomes | null> {
        return MarketingCampaignFetcher.fetchArticlePublicationOutcomes(campaignId, articleId)
    }

    static createArticleSearchObservation(
        campaignId: string,
        articleId: string,
        observation: MarketingArticleSearchObservationInput
    ): Promise<any> {
        return MarketingCampaignFetcher.createArticleSearchObservation(campaignId, articleId, observation)
    }

    static beginGa4Oauth(campaignId: string, credentialId?: string): Promise<string> {
        return MarketingCampaignFetcher.beginGa4Oauth(campaignId, credentialId).then(res => {
            return (res && res.authorization_url) || null
        })
    }

    static beginGscOauth(campaignId: string, credentialId?: string): Promise<string> {
        return MarketingCampaignFetcher.beginGscOauth(campaignId, credentialId).then(res => {
            return (res && res.authorization_url) || null
        })
    }

    static fetchGscSites(campaignId: string, credentialId: string): Promise<GscSiteOption[]> {
        return MarketingCampaignFetcher.fetchGscSites(campaignId, credentialId).then(res => {
            return (res && res.sites) || []
        })
    }

    static connectGscSites(campaignId: string, credentialId: string, siteUrls: string[]): Promise<MarketingIntegrationStatus[]> {
        return MarketingCampaignFetcher.connectGscSites(campaignId, credentialId, siteUrls).then(
            res => (res && res.integrations) || []
        )
    }

    static fetchGa4Properties(campaignId: string, credentialId: string, pageToken?: string): Promise<any> {
        return MarketingCampaignFetcher.fetchGa4Properties(campaignId, credentialId, pageToken)
    }

    static connectGa4Properties(
        campaignId: string,
        credentialId: string,
        propertyIds: string[]
    ): Promise<MarketingIntegrationStatus[]> {
        return MarketingCampaignFetcher.connectGa4Properties(campaignId, credentialId, propertyIds).then(
            res => (res && res.integrations) || []
        )
    }

    // fullRefresh はGA4だけが対応。180日分のdailyと期間集計をすべて取り直す。
    static syncIntegration(
        campaignId: string,
        integrationId: string,
        options: { fullRefresh?: boolean } = {}
    ): Promise<boolean> {
        const params = options.fullRefresh ? { full_refresh: true } : {}
        return MarketingCampaignFetcher.syncIntegration(campaignId, integrationId, params).then(res => {
            return !!res
        })
    }

    static syncAllIntegrations(campaignId: string, params: any = {}): Promise<MarketingIntegrationBulkSyncResult | null> {
        return MarketingCampaignFetcher.syncAllIntegrations(campaignId, params).then(res => res || null)
    }

    static revokeGa4Credential(credentialId: string): Promise<boolean> {
        return MarketingCampaignFetcher.revokeGa4Credential(credentialId).then(res => {
            return !!res
        })
    }

    static revokeGscCredential(credentialId: string): Promise<boolean> {
        return MarketingCampaignFetcher.revokeGoogleCredential(credentialId).then(res => {
            return !!res
        })
    }

    static pauseCampaign(id: string): Promise<MarketingCampaign> {
        return MarketingCampaign.updateCampaign(id, { status: MarketingCampaign.STATUS_PAUSED })
    }

    static activateCampaign(id: string): Promise<MarketingCampaign> {
        return MarketingCampaign.updateCampaign(id, { status: MarketingCampaign.STATUS_ACTIVE })
    }

    static archiveCampaign(id: string): Promise<MarketingCampaign> {
        return MarketingCampaignFetcher.archiveCampaign(id).then(res => {
            if (!res || !res.campaign) return null
            return MarketingCampaign.fetchFromJson([res.campaign])[0]
        })
    }

    get serviceNames(): string[] {
        return (this.service_types || []).map(serviceType => {
            const option = MarketingCampaign.SERVICE_OPTIONS.find(dic => dic.key == serviceType)
            return option ? option.name : MarketingCampaign.LEGACY_SERVICE_NAMES[serviceType] || serviceType
        })
    }
}
